import React, { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Features from './components/Features';
import InteractiveDemo from './components/InteractiveDemo';
import ApiReference from './components/ApiReference';
import Installation from './components/Installation';
import UseCases from './components/UseCases';
import Statistics from './components/Statistics';
import CodeExample from './components/CodeExample';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';

gsap.registerPlugin(ScrollTrigger);

function App() {
  useEffect(() => {
    const ctx = gsap.context(() => {

      // ─── Hero: parallax glow orbs drift on scroll ───
      const heroOrbs = document.querySelectorAll('.hero-orb');
      heroOrbs.forEach((orb, i) => {
        const speeds = [150, 250, 100];
        gsap.to(orb, {
          y: speeds[i] || 150,
          ease: 'none',
          scrollTrigger: {
            trigger: '.animated-gradient',
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      });

      // ─── Section intros: heading + subtitle stagger reveal ───
      document.querySelectorAll('.section-intro').forEach((intro) => {
        gsap.fromTo(
          Array.from(intro.children),
          { opacity: 0, y: 30 },
          {
            scrollTrigger: { trigger: intro, start: 'top 85%' },
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.15,
            ease: 'power3.out',
          }
        );
      });

      // ─── Standalone section headings ───
      document.querySelectorAll('.section-heading').forEach((heading) => {
        gsap.fromTo(heading,
          { opacity: 0, y: 30 },
          {
            scrollTrigger: { trigger: heading, start: 'top 85%' },
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
          }
        );
      });

      // ─── Feature cards: horizontal scroll right-to-left while page scrolls ───
      const featuresTrack = document.querySelector('.features-track');
      if (featuresTrack) {
        gsap.to(featuresTrack, {
          x: () => -(featuresTrack.scrollWidth - featuresTrack.parentElement.offsetWidth),
          ease: 'none',
          scrollTrigger: {
            trigger: '#features',
            start: 'top top',
            end: () => `+=${featuresTrack.scrollWidth - featuresTrack.parentElement.offsetWidth}`,
            pin: true,
            scrub: 0.5,
            invalidateOnRefresh: true,
          },
        });
      }

      // ─── Demo: tabs slide up, content panel scales in ───
      const demoTabs = document.querySelector('.demo-tabs');
      if (demoTabs) {
        gsap.fromTo(demoTabs,
          { opacity: 0, y: 30 },
          {
            scrollTrigger: { trigger: '#demo', start: 'top 70%' },
            opacity: 1,
            y: 0,
            duration: 0.6,
            delay: 0.3,
            ease: 'power2.out',
          }
        );
      }

      const demoContent = document.querySelector('.demo-content');
      if (demoContent) {
        gsap.fromTo(demoContent,
          { opacity: 0, y: 40, scale: 0.97 },
          {
            scrollTrigger: { trigger: '#demo', start: 'top 65%' },
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            delay: 0.45,
            ease: 'power2.out',
          }
        );
      }

      // ─── API categories: alternate slide from left / right ───
      const apiCategories = document.querySelectorAll('.api-category');
      apiCategories.forEach((cat, i) => {
        gsap.fromTo(cat,
          { opacity: 0, x: i % 2 === 0 ? -60 : 60 },
          {
            scrollTrigger: { trigger: cat, start: 'top 82%' },
            opacity: 1,
            x: 0,
            duration: 0.7,
            ease: 'power2.out',
          }
        );
      });

      // ─── Installation: command slides left, code slides right, buttons stagger ───
      const installCommand = document.querySelector('.install-command');
      if (installCommand) {
        gsap.fromTo(installCommand,
          { opacity: 0, x: -50 },
          {
            scrollTrigger: { trigger: '#install', start: 'top 70%' },
            opacity: 1,
            x: 0,
            duration: 0.7,
            ease: 'power2.out',
          }
        );
      }

      const installCode = document.querySelector('.install-code');
      if (installCode) {
        gsap.fromTo(installCode,
          { opacity: 0, x: 50 },
          {
            scrollTrigger: { trigger: installCode, start: 'top 82%' },
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power2.out',
          }
        );
      }

      const installActions = document.querySelector('.install-actions');
      if (installActions) {
        gsap.fromTo(
          Array.from(installActions.children),
          { opacity: 0, y: 20 },
          {
            scrollTrigger: { trigger: installActions, start: 'top 88%' },
            opacity: 1,
            y: 0,
            duration: 0.5,
            stagger: 0.15,
            ease: 'power2.out',
          }
        );
      }

      // ─── Use case cards: alternate slide from left / right ───
      const useCaseCards = document.querySelectorAll('.use-case-card');
      useCaseCards.forEach((card, i) => {
        gsap.fromTo(card,
          { opacity: 0, x: i % 2 === 0 ? -40 : 40, y: 30 },
          {
            scrollTrigger: { trigger: card, start: 'top 85%' },
            opacity: 1,
            x: 0,
            y: 0,
            duration: 0.7,
            ease: 'power2.out',
          }
        );
      });

      // ─── Statistics: cards scale-bounce in with stagger ───
      const statCards = document.querySelectorAll('.stat-card');
      if (statCards.length > 0) {
        gsap.fromTo(statCards,
          { opacity: 0, scale: 0.7, y: 30 },
          {
            scrollTrigger: {
              trigger: statCards[0].closest('section'),
              start: 'top 75%',
            },
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.12,
            ease: 'back.out(1.4)',
          }
        );
      }

      // Statistics counter animation (delayed to follow card entrance)
      const statNumbers = document.querySelectorAll('.stat-number');
      statNumbers.forEach((el) => {
        const target = parseInt(el.getAttribute('data-target'), 10);
        if (isNaN(target)) return;

        const originalText = el.textContent;
        const suffix = originalText.includes('+')
          ? '+'
          : originalText.includes('%')
          ? '%'
          : '';

        ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          onEnter: () => {
            const obj = { val: 0 };
            gsap.to(obj, {
              val: target,
              duration: 2,
              delay: 0.6,
              ease: 'power2.out',
              onUpdate: () => {
                el.textContent =
                  target >= 100
                    ? Math.floor(obj.val).toLocaleString() + suffix
                    : Math.floor(obj.val) + suffix;
              },
            });
          },
          once: true,
        });
      });

      // ─── Code Example: perspective tilt reveal ───
      const codeEditor = document.querySelector('.code-editor');
      if (codeEditor) {
        gsap.fromTo(codeEditor,
          { opacity: 0, y: 60, rotateX: 8, transformPerspective: 800 },
          {
            scrollTrigger: { trigger: codeEditor, start: 'top 82%' },
            opacity: 1,
            y: 0,
            rotateX: 0,
            duration: 1,
            ease: 'power3.out',
          }
        );
      }

      // ─── CTA: children stagger wave ───
      const ctaContent = document.querySelector('.cta-content');
      if (ctaContent) {
        gsap.fromTo(
          Array.from(ctaContent.children),
          { opacity: 0, y: 40 },
          {
            scrollTrigger: { trigger: ctaContent, start: 'top 80%' },
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.12,
            ease: 'power3.out',
          }
        );
      }

      // ─── Footer: columns stagger up + bottom bar fade in ───
      const footerCols = document.querySelectorAll('.footer-grid > div');
      if (footerCols.length > 0) {
        gsap.fromTo(footerCols,
          { opacity: 0, y: 30 },
          {
            scrollTrigger: { trigger: '.footer-grid', start: 'top 88%' },
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.15,
            ease: 'power2.out',
          }
        );
      }

      const footerBottom = document.querySelector('.footer-bottom');
      if (footerBottom) {
        gsap.fromTo(footerBottom,
          { opacity: 0, y: 20 },
          {
            scrollTrigger: { trigger: footerBottom, start: 'top 92%' },
            opacity: 1,
            y: 0,
            duration: 0.5,
            delay: 0.3,
            ease: 'power2.out',
          }
        );
      }

    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="App">
      <Navbar />
      <Hero />
      <Features />
      <InteractiveDemo />
      <ApiReference />
      <Installation />
      <UseCases />
      <Statistics />
      <CodeExample />
      <CallToAction />

      {/* Author section */}
      <section className="py-16 text-center">
        <img
          src="/author.jpg"
          alt="Sumudu Kulathunga"
          className="w-20 h-20 rounded-full object-cover ring-2 ring-white/10 mx-auto mb-4"
        />
        <p className="text-white font-semibold text-base">Sumudu Kulathunga</p>
        <p className="text-slate-500 text-sm mt-1">Author &amp; Maintainer</p>
      </section>

      <Footer />
    </div>
  );
}

export default App;
